import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { SubscriptionStore } from '../../core/stores/subscription.store';

/**
 * Lives near the top of the page to nudge BU admins about upcoming or current
 * subscription trouble — expiring soon, grace period, expired. Auto-hides when
 * the subscription is healthy.
 */
@Component({
  selector: 'app-subscription-status-banner',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <ng-container *ngIf="kind() as k">
      <div [ngClass]="bannerClass()"
           class="rounded-lg border px-4 py-3 flex items-start gap-3 text-sm">
        <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div class="flex-1">
          <div class="font-medium">{{ titleText() }}</div>
          <p class="mt-0.5 opacity-90">{{ bodyText() }}</p>
          <div class="flex flex-wrap gap-2 mt-2">
            <a routerLink="/billing/submit-payment"
               class="px-3 py-1 text-xs font-medium bg-white/80 rounded hover:bg-white">
              Submit payment
            </a>
            <a *ngIf="k === 'expired'"
               routerLink="/billing/request-exemption"
               class="px-3 py-1 text-xs font-medium bg-white/80 rounded hover:bg-white">
              Request exemption
            </a>
            <a routerLink="/billing"
               class="px-3 py-1 text-xs font-medium opacity-75 hover:opacity-100">
              See details →
            </a>
          </div>
        </div>
      </div>
    </ng-container>
  `
})
export class SubscriptionStatusBannerComponent {
  private store = inject(SubscriptionStore);

  readonly kind = computed<'expiring' | 'grace' | 'expired' | 'cancelPending' | null>(() => {
    const sub = this.store.current();
    if (!sub) return null;
    if (sub.status === 'Expired') return 'expired';
    if (sub.status === 'Grace') return 'grace';
    if (sub.status === 'CancelPending') return 'cancelPending';
    const days = this.store.daysRemaining();
    if (sub.status === 'Active' && days != null && days <= 14 && days >= 0) return 'expiring';
    return null;
  });

  readonly bannerClass = computed(() => {
    switch (this.kind()) {
      case 'expired':       return 'bg-red-50 border-red-300 text-red-800';
      case 'grace':         return 'bg-orange-50 border-orange-300 text-orange-800';
      case 'expiring':      return 'bg-amber-50 border-amber-300 text-amber-800';
      case 'cancelPending': return 'bg-gray-50 border-gray-300 text-gray-700';
      default:              return '';
    }
  });

  readonly titleText = computed(() => {
    switch (this.kind()) {
      case 'expired':       return 'Subscription expired';
      case 'grace':         return 'Grace period — renew soon';
      case 'expiring':      return `Subscription expires in ${this.store.daysRemaining()} day(s)`;
      case 'cancelPending': return 'Cancellation scheduled';
      default:              return '';
    }
  });

  readonly bodyText = computed(() => {
    const sub = this.store.current();
    if (!sub) return '';
    switch (this.kind()) {
      case 'expired':
        return `Your ${sub.planName} subscription has ended. Submit a payment or request an exemption to restore access.`;
      case 'grace':
        return `Your ${sub.planName} subscription is past due. Renew before ${this.formatDate(sub.gracePeriodEndsAt)} to keep your features.`;
      case 'expiring':
        return `Renew now to avoid an interruption to ${sub.planName}. Period ends ${this.formatDate(sub.currentPeriodEnd)}.`;
      case 'cancelPending':
        return `Your subscription will end on ${this.formatDate(sub.currentPeriodEnd)}. You can resume any time before then.`;
      default:
        return '';
    }
  });

  private formatDate(d?: string | null): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString();
  }
}
